import {useContext, useEffect} from 'react';
import {SearchPageTypeContext} from './search';
import {batch, useDispatch, useSelector} from 'react-redux';
import {
    expandCollapseFacetGroupAction,
    fetchDocumentDetails,
    setCurrentPageNumberAction,
    sortChangeAction,
    startSearchAction,
    switchResultDetailsTabAction,
    updateAllFacetsAction
} from './duck/search-actions';
import {HibuSearchRequest, HibuSearchRequestSort, ProdDocument, SearchSource, SortType, TabType} from './search-types';
import {SearchPageType} from './search-config';
import {useLocation, useParams} from 'react-router-dom';
import {doGetAppVersionsRequest, doLoadContainersRequest} from '../services/settings-service';
import {ReduxIndexedNormalizedType} from '../app-types';
import {HistoryEntry} from './duck/history-types';
import {FacetState, HibuFacetFilter, HibuFacetFilterFromUrl} from '../facets/facetTypes';
import {SavedSearchWithId} from './save-search/duck/saveSearchComponent-types';
import {ContainersResponse} from '../settings/duck/settings-types';
import {parseSortFieldString} from './search-order-parser';
import {parseFacetFilterString} from './url/facet-filter-parser';
import {doFetchFacets, doSearchRequest, SEARCH_BATCH_SIZE} from '../services/search-service';
import {getSort, getSpellcheckParams} from './search.utils';
import {isSearchInFavorites} from '../utils/utils';
import {useStateContainerFacets, useStateContainerName} from '../settings/duck/settings-hooks';
import {ClickableElement} from './search-results/search-result-details/content/clickable-section-list/clickableSectionList';
import {PreviewDocument} from './search-results/search-preview/doc-preview-section/docPreviewRow-types';
import {parsePageNumberString} from './url/page-number-parser';
import {SearchCompletedAction} from './duck/search-types';
import {
    ENCODED_COMMAS,
    QUERY_ENTRY_SEPARATOR,
    URL_FILTER_PARAM,
    URL_PAGE_PARAM,
    URL_QUERY_PARAM,
    URL_SORT_PARAM,
    URL_TAB_PARAM
} from './const';
import {SEARCH_PATH} from '../config';
import {parseOpenTab} from './url/tab-type-parse';
import {getCurrentLanguage} from '../i18n-service';

export function useSearchPageType(): SearchPageType {
    return useContext(SearchPageTypeContext);
}

/**
 * Returns the currently selected search result item as {@link ProdDocument} or {@code undefined}
 * if none is selected.
 */
export function useSelectedDocContext(): ProdDocument | undefined {
    const searchPageType = useSearchPageType();
    return useSelectedDocument(searchPageType);
}

const useSelectedDocument = (type: SearchPageType): ProdDocument | undefined => {
    return useSelector((state) => state.search[type].expandedProdDocument);
};

export function useStateExpandedProdDocumentId(): string | undefined {
    return useSelector((state) => state.search.BASIC.expandedProdDocument?.id);
}

export function useStateHistoryEntries(): HistoryEntry[] {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].historyEntries);
}

export function useStateSavedSearches(): SavedSearchWithId[] | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].savedSearches);
}

export function useStateSavedSearchesSharedByOthers(): SavedSearchWithId[] | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].savedSearchesSharedByOthers);
}

export function useStateShowScrollButton(): boolean {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].showScrollButton);
}

export function useStateShowFullDocument(): boolean {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].showFullDocument);
}

export function useStateUpdateDocumentInProgress(): boolean {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].updateDocumentInProgress);
}

export function useStateDocs(): ReduxIndexedNormalizedType<ProdDocument> {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].docs);
}

export function useStateDocsCount(): number {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].docs.allKeys.length);
}

export function useStateFacets(): FacetState {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].facets);
}

export function useStateQuery(): string {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].query);
}

export function useStateInDocumentQuery(): string | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].inDocumentQuery);
}

export function useStateLastSearchedQuery(): string {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].lastSearchedQuery);
}

export function useCurrentSortType(): SortType {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].currentSortType);
}

export function useDefaultSortType(): SortType | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].sortTypes?.[0] || undefined);
}

export function useStateQueryFields(): string[] | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].queryFields);
}

export function useStateQueryPending(): boolean {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].queryPending);
}

export function useStateFacetsPending(): boolean {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].facetsPending);
}

export function useStateSpellCheckResult(): string {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].spellCheckResult);
}

export function useStateResultFor(): string {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].resultFor);
}

export function useStateSources(): SearchSource[] {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].sources);
}

export function useStateTotalFound(): number | null | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].totalFound);
}

export function useStateFacetListExpanded(): boolean {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].facetListExpanded);
}

export function useStateDocDetailsExpanded(): boolean {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].documentDetailsExpanded);
}

export function useStatePreviewDocument(): PreviewDocument | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].previewDocument);
}

export function useStatePreviewDocumentWithHighlights(): PreviewDocument | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].previewDocumentWithHighlights);
}

export function useStateDocumentQueryHighlights() {
    const searchPageType = useSearchPageType();
    return useSelector(state => state.search[searchPageType].documentQueryHighlights);
}

export function useStateDocumentSelectedTags() {
    const searchPageType = useSearchPageType();
    return useSelector(state => state.search[searchPageType].documentSelectedTags);
}

export function useStateShowAnnotations() {
    const searchPageType = useSearchPageType();
    return useSelector(state => state.search[searchPageType].showAnnotations);
}

export function useStateHighlightId() {
    const searchPageType = useSearchPageType();
    return useSelector(state => state.search[searchPageType].highlightId);
}

export function useStateResultDetailsTab(): TabType {
    const searchPageType = useSearchPageType();
    return useSelector((state) => state.search[searchPageType].openResultTab);
}

export function useStateCurrentPageNumber(): number {
    const searchPageType = useSearchPageType();
    return useSelector((state) => {
        return state.search[searchPageType].currentPageNumber;
    });
}

export function useStateCurrentPageRotation(): number {
    const searchPageType = useSearchPageType();
    return useSelector((state) => {
        const pageNumber = state.search[searchPageType].currentPageNumber;
        const pageRotation = state.search[searchPageType].pageRotations[pageNumber - 1];
        return pageRotation ? pageRotation : 0;
    });
}

export function useStateNumberOfPages(): number {
    const searchPageType = useSearchPageType();
    return useSelector((state) => {
        return state.search[searchPageType].numberOfPages;
    });
}

export function useStateScrollTarget(): ClickableElement | null {
    const searchPageType = useSearchPageType();
    return useSelector((state) => {
        return state.search[searchPageType].scrollTarget;
    });
}

export function useStateDoResetTransform(): boolean {
    const searchPageType = useSearchPageType();
    return useSelector((state) => {
        return state.search[searchPageType].resetTransform;
    });
}

export function useStateSortTypes(): SortType[] | undefined {
    const searchPageType = useSearchPageType();
    return useSelector((state) => {
        return state.search[searchPageType].sortTypes;
    });
}

export function useStateIsDocumentLoading() {
    const searchPageType = useSearchPageType();
    return useSelector((state) => {
        return state.search[searchPageType].isDocumentLoading;
    });
}

export function useStateUseAutoCorrection() {
    const searchPageType = useSearchPageType();
    return useSelector((state) => {
        return state.search[searchPageType].useAutoCorrection;
    });
}

export function useQueryParams() {
    const search = useLocation().search.replaceAll(ENCODED_COMMAS, QUERY_ENTRY_SEPARATOR);
    return new URLSearchParams(search);
}

export function useSearchURL() {
    let path = `${window.location.pathname}`;
    path = path.substring(0, path.indexOf(SEARCH_PATH)) + SEARCH_PATH; // ensure that we get any context path like '/app' and always end with '/search'
    return `${window.location.origin}${path}`;
}

/**
 * Synchronizes the URL with the search query in the search bar and vice versa.
 * Every time the search query in the search bar is changed, the URL will be updated.
 * Also, when initially visiting the search page, it will initialize the query from the URL.
 */
export function useSynchronizeSearchFromUrl(searchPageType: SearchPageType): void {
    const dispatch = useDispatch();
    const container = useStateContainerName();
    const language = getCurrentLanguage();
    const {query: formerUrlSearchExpression} = useParams<{ query: string }>();
    const currentSearchExpression = useStateQuery();
    const uriParams = useQueryParams();
    const searchExpression = uriParams.get(URL_QUERY_PARAM) || currentSearchExpression || formerUrlSearchExpression || '';
    const facetFilterParam: string = uriParams.get(URL_FILTER_PARAM) || '';
    const sortTypesFromConfiguration: SortType[] | undefined = useStateSortTypes();
    const sortFieldParam: string = uriParams.get(URL_SORT_PARAM) || '';
    const hibuFacetFilterFromUrl: HibuFacetFilterFromUrl = parseFacetFilterString(facetFilterParam);
    const currentSortType: SortType = parseSortFieldString(sortFieldParam, sortTypesFromConfiguration);
    const defaultSortField = useDefaultSortType();
    const sort = getSort(searchExpression, currentSortType, defaultSortField);
    const spellcheckEnabled = !isSearchInFavorites(useStateSources());
    const containerFacets = useStateContainerFacets();
    const pageParam: string = uriParams.get(URL_PAGE_PARAM) || '';
    const pageNumber: number = parsePageNumberString(pageParam);
    const tabParam: TabType = parseOpenTab(uriParams.get(URL_TAB_PARAM) || '', useStateResultDetailsTab());

    useEffect(() => {
        batch(() => {
            dispatch(startSearchAction(searchPageType, searchExpression));
            dispatch(sortChangeAction(searchPageType, currentSortType));
            dispatch(updateAllFacetsAction(searchPageType, hibuFacetFilterFromUrl.filterValuesByFieldName, containerFacets));
            dispatch(setCurrentPageNumberAction(pageNumber));
            dispatch(switchResultDetailsTabAction(tabParam));
            for (const hibuFiltersKey in hibuFacetFilterFromUrl.hibuFilters) {
                dispatch(expandCollapseFacetGroupAction(hibuFiltersKey, searchPageType));
            }
        });
        const request = createSearchRequestFromUrl(container, language, searchExpression,
            hibuFacetFilterFromUrl.hibuFilters, sort, spellcheckEnabled);
        dispatch(doFetchFacets(searchPageType, request));
        dispatch(doSearchRequest(searchPageType, request, true, true))
            ?.then((result: SearchCompletedAction | undefined) => {
                //if id filter is present in url, then get the current document (only one) and expand it
                if (hibuFacetFilterFromUrl.filterValuesByFieldName.id) {
                    const docs = result?.searchResult.docs;
                    const doc = (docs ? docs[0] : null) as ProdDocument | undefined;
                    dispatch(fetchDocumentDetails(doc));
                }
            });
        },
        // empty commit 7f0eaee4d8fe6d1e18fc2dc8f3b59dd63e7aa1ad, useEffect to be used only once
        []);
}

function createSearchRequestFromUrl(container: string, language: string, searchExpression: string,
                                    filter: HibuFacetFilter, sort: HibuSearchRequestSort,
                                    spellcheckEnabled: boolean): HibuSearchRequest {
    return {
        container: container,
        displayLanguage: language,
        query: searchExpression,
        from: 0,
        size: SEARCH_BATCH_SIZE,
        filters: filter,
        sort: sort,
        spellcheckEnabled: spellcheckEnabled,
        spellcheckParams: getSpellcheckParams(true),
    };
}

export function useSettingsContainerRequest(): void {
    const dispatch = useDispatch();
    const containerName = useSelector((state) => state.settings.containerName);
    const searchPageType = useSearchPageType();
    dispatch(doLoadContainersRequest(searchPageType, containerName));
}

export function useLoadVersionsRequest(callback: (response: ContainersResponse) => void): void {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(doGetAppVersionsRequest(callback));
    });
}
